import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Calendar,
  BarChart2,
  History,
  Trophy,
  ClipboardList,
  Search,
  Menu,
  X,
  Award as RecordIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthButton from './AuthButton';

const isTabActive = (activeTab, tabKey) => activeTab === tabKey;

const navLinkClass = (active, mobile = false) => {
  const base = mobile
    ? 'flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all duration-200'
    : 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap';

  // Mobile menu renders on white background: use dark text and blue active background
  if (mobile) {
    return active
      ? `${base} bg-blue-600 text-white font-medium`
      : `${base} text-gray-700 hover:bg-gray-50 hover:text-gray-900`;
  }

  // Desktop header uses a blue gradient; prefer white-on-blue for clarity and a subtle active highlight
  return active
    ? `${base} bg-white/10 text-white font-semibold ring-1 ring-white/10`
    : `${base} text-white/70 hover:bg-white/10 hover:text-white`;
};

const Navbar = ({
  activeTab,
  onTemporadasClick,
  onHistoricoClick,
  onAllMnlsClick,
  onJugadoresClick,
  onRecordsClick,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const publicItems = [
    {
      key: 'temporadas',
      to: '/temporadas',
      label: 'Temporadas',
      icon: Calendar,
      onClick: onTemporadasClick,
    },
    {
      key: 'historico',
      to: '/historico',
      label: 'Histórico',
      icon: History,
      onClick: onHistoricoClick,
    },
    {
      key: 'allMnls',
      to: '/all-mnls',
      label: 'All MNLS',
      icon: Trophy,
      onClick: onAllMnlsClick,
    },
    {
      key: 'detalleJugador',
      to: '/jugadores',
      label: 'Detalle del Jugador',
      icon: BarChart2,
      onClick: onJugadoresClick,
    },
    {
      key: 'records',
      to: '/records',
      label: 'Records',
      icon: RecordIcon,
      onClick: onRecordsClick,
    },
    {
      key: 'busquedaPartidos',
      to: '/busqueda-partidos',
      label: 'Búsqueda de partidos',
      icon: Search,
    },
  ];

  const authItems = isAuthenticated
    ? [
        {
          key: 'registrarPartido',
          to: '/registrar-partido',
          label: 'Registrar Partido',
          icon: ClipboardList,
        },
      ]
    : [];

  const allItems = [...publicItems, ...authItems];

  const renderNavLink = (item, mobile = false) => {
    const Icon = item.icon;
    const active = isTabActive(activeTab, item.key);

    return (
      <Link
        key={item.key}
        to={item.to}
        onClick={() => {
          item.onClick?.();
          if (mobile) closeMenu();
        }}
        className={navLinkClass(active, mobile)}
      >
        <Icon className={`${mobile ? 'h-5 w-5' : 'h-4 w-4'} shrink-0`} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-gradient-to-r from-blue-800 via-blue-700 to-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-[4.5rem]">
            <Link to="/all-mnls" onClick={onAllMnlsClick} className="flex items-center gap-3 min-w-0 group">
              <img
                src="/escudoMNLS.png"
                alt="Escudo MNLS"
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain shrink-0 drop-shadow-md group-hover:scale-105 transition-transform duration-200"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold tracking-tight truncate leading-tight">
                  MNLS Stats
                </h1>
                <p className="text-blue-100/90 text-xs sm:text-sm hidden xs:block truncate">
                  Estadísticas de Fútbol
                </p>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {allItems.map((item) => renderNavLink(item))}
              <div className="ml-2 pl-2 border-l border-blue-500/40">
                <AuthButton variant="nav" />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 active:bg-white/20 transition-colors"
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!menuOpen}
      >
        <button
          type="button"
          className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
          onClick={closeMenu}
          aria-label="Cerrar menú"
        />

        <nav
          className={`absolute top-0 right-0 h-full w-[min(100%,20rem)] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
            <span className="font-semibold text-gray-800">Menú</span>
            <button
              type="button"
              onClick={closeMenu}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {allItems.map((item) => renderNavLink(item, true))}
          </div>

          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <AuthButton variant="menu" onAction={closeMenu} />
          </div>
        </nav>
      </div>
    </>
  );
};

export default Navbar;
